/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { request } from '@mairistem/tools';

import { AssistantContext } from '@/contexts';
import { getHubeeTeledossier } from '@/routes';

/**
 * Détecte l'entrée dans l'assistant en mode Hubee et charge le télédossier.
 *
 * L'assistant est en mode Hubee dès que l'URL porte `identifiantTeledossier`
 * (ajouté par la liste des demandes et par la fiche demande de
 * `@mairistem/administres-utils`). Le télédossier chargé est posé dans le
 * contexte de l'assistant, où `AssistantContextProvider` s'en sert pour
 * préremplir les champs.
 */
const useHubeeTeledossier = () => {
  const history = useHistory();
  const { search } = history?.location || {};

  const {
    setTeledossierData,
    setIsLoadingHubeeData,
    setSelectedDeclarationType,
    setSelectedPacs,
  } = React.useContext(AssistantContext);

  const identifiantTeledossier = React.useMemo(() => {
    if (!search) return null;

    return new URLSearchParams(search).get('identifiantTeledossier');
  }, [search]);

  // Une demande Hubee est toujours une création de projet de PACS : les deux
  // premières étapes (type de déclaration, sélection du PACS) n'ont pas lieu
  // d'être, on pose donc directement leur résultat. `-1` = création, pas de
  // PACS existant repris comme base.
  React.useEffect(() => {
    if (!identifiantTeledossier) {
      return;
    }

    setSelectedDeclarationType('projet');
    setSelectedPacs(-1);
  }, [identifiantTeledossier, setSelectedDeclarationType, setSelectedPacs]);

  /** Télédossier reçu : l'assistant peut s'afficher, prérempli. */
  const [isTeledossierCharge, setIsTeledossierCharge] = React.useState(false);

  /** Échec du chargement : l'assistant ne doit pas permettre de créer un PACS. */
  const [hasErreurTeledossier, setHasErreurTeledossier] = React.useState(false);

  React.useEffect(() => {
    if (!identifiantTeledossier) {
      return undefined;
    }

    // Réponse d'une demande précédente arrivée après un changement d'URL :
    // on l'ignore pour ne pas préremplir avec la mauvaise demande.
    let ignore = false;

    setIsTeledossierCharge(false);
    setHasErreurTeledossier(false);
    setIsLoadingHubeeData(true);

    request
      .get(getHubeeTeledossier(identifiantTeledossier))
      .then((response: any) => {
        if (ignore) return;
        setTeledossierData(response);
        setIsTeledossierCharge(true);
      })
      .catch(() => {
        if (ignore) return;
        setHasErreurTeledossier(true);
        toast.error('Erreur lors de la récupération du télédossier');
      })
      .finally(() => {
        if (ignore) return;
        setIsLoadingHubeeData(false);
      });

    return () => {
      ignore = true;
    };
  }, [identifiantTeledossier, setTeledossierData, setIsLoadingHubeeData]);

  return {
    /** L'assistant est ouvert pour traiter une demande Hubee */
    isHubeeMode: !!identifiantTeledossier,
    identifiantTeledossier,
    isTeledossierCharge,
    hasErreurTeledossier,
  };
};

export default useHubeeTeledossier;
